import { cn } from "@tokenterminal/core/utils/cn"
import Image from "next/image"
import Link from "next/link"
import { formatDate } from "@/components/resources/components/ui/utils"
import type { Teaser } from "@/components/resources/data/posts.types"
import { getOGImageRatioWidth } from "@/constants/blog-image"
import { BASE_BLOG_PATH } from "@/constants/urls"
import { BlogImage } from "../BlogImage"

const TeaserLink = ({
  children,
  href,
}: {
  href: string
  children: React.ReactNode
}) => (
  <Link
    href={`${BASE_BLOG_PATH}${href}`}
    className="absolute inset-0 z-1"
    aria-label={typeof children === "string" ? children : undefined}
  >
    <span className="sr-only">{children}</span>
  </Link>
)

const Published = ({
  publishedAt,
  className,
}: Pick<Teaser, "publishedAt"> & { className?: string }) =>
  publishedAt ? (
    <p className={cn("text-sm text-fg-secondary", className)}>
      <time dateTime={formatDate(new Date(publishedAt))}>
        {formatDate(new Date(publishedAt), true)}
      </time>
    </p>
  ) : null

export const SmallTeaser = ({
  teaser: {
    title,
    image,
    slug,
    category,
    teaserText,
    publishedAt,
    releaseNumber,
    projectMeta,
  },
  className,
}: {
  teaser: Teaser
  className?: string
}) => {
  return (
    <Link
      href={`${BASE_BLOG_PATH}${category ? `/${category}` : ""}/${slug}`}
      className={cn(
        "flex flex-col w-full rounded-lg overflow-hidden relative",
        className
      )}
    >
      {typeof image === "string" ? (
        <Image
          src={image}
          alt={title}
          className="w-full rounded-lg mb-4 aspect-blog-cover object-cover object-center"
          width={413}
          height={getOGImageRatioWidth(413)}
          quality={100}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 413px"
        />
      ) : (
        <BlogImage
          width={413}
          height={getOGImageRatioWidth(413)}
          alt={title}
          className="w-full rounded-lg mb-4 aspect-blog-cover object-cover object-center"
          {...{ category, image, releaseNumber, projectMeta }}
        />
      )}
      <div className="flex flex-col gap-2 flex-1">
        <h2 className="text-base font-medium leading-tight">{title}</h2>
        <p className="text-fg-secondary text-sm line-clamp-2 relative z-5">
          {teaserText}
        </p>
        <Published publishedAt={publishedAt} />
      </div>
    </Link>
  )
}

export const LargeTeaser = ({
  teaser: {
    title,
    image,
    slug,
    category,
    teaserText,
    publishedAt,
    releaseNumber,
    projectMeta,
  },
}: {
  teaser: Teaser
}) => {
  return (
    <div className="flex flex-col md:flex-row rounded-lg overflow-hidden bg-neutral-900">
      <div className="relative md:w-[640px]">
        <BlogImage
          width="640"
          height="444"
          alt={title}
          className="w-full h-full object-cover"
          {...{ category, image, releaseNumber, projectMeta }}
        />
      </div>
      <div className="relative flex justify-center items-center">
        <TeaserLink href={`${category ? `/${category}` : ""}/${slug}`}>
          {title}
        </TeaserLink>
        <div className="flex flex-col items-start justify-center py-8 px-4 md:py-12 md:px-6">
          <span className="px-2 py-1 text-xs font-medium bg-neutral-800 text-neutral-50 rounded-sm">
            Featured
          </span>
          <h2 className="text-2xl font-medium mt-2">{title}</h2>
          {teaserText && (
            <p className="text-fg-secondary text-sm mt-2 relative z-5">
              {teaserText}
            </p>
          )}
          <Published publishedAt={publishedAt} className="mt-2" />
        </div>
      </div>
    </div>
  )
}
